
import { defineComponent, onMounted, ref, getCurrentInstance } from "vue";
import AppLayout from "@/layouts/AppLayout.vue";
import { useAuth0 } from "@auth0/auth0-vue";
import { useAuthStore } from "./stores/authStore";
import { useApiStore } from "./stores/apiStore";
import { useSiteStore } from "./stores/siteStore";
import { useWslinkStore } from "@/stores/ssaStore/wslink";
import { useSsaStore } from "@/stores/ssaStore";
import { useActiveStore } from "./stores/ssaStore/active";

import { watch } from "vue";
import { useRouter, useRoute, LocationQueryValue } from "vue-router";
import axios from "axios";
import { AxiosError } from "axios";
import decode from "jwt-decode";
import _ from "lodash";
import { useConfigStore } from "./stores/configStore";
import { useToastStore } from "@/stores/toastStore";
import { usePvwStore } from "./stores/ssaStore/pvw";
import { useSaveStateStore } from "./stores/ssaStore/saveState";
import { useBusyStore } from "./stores/ssaStore/busy";
import { CustomErrorCodes } from "./utils/CustomErrorCodes";
export default defineComponent({
	setup() {
		const ready = ref(false);
		const router = useRouter();

		const route = useRoute();
		const auth0 = useAuth0();
		const api = useApiStore();
		const authStore = useAuthStore();
		const siteStore = useSiteStore();
		const configStore = useConfigStore();
		const toast = useToastStore();
		const pvw = usePvwStore();
		const wsLink = useWslinkStore();
		const ssaStore = useSsaStore();
		const saveState = useSaveStateStore();
		const active = useActiveStore();
		const busy = useBusyStore();
		const hasError = ref(false);
		const eulaDialog = ref(false);

		const setIsReady = (user: AuthUser) => {
			if (user.email != null) {
				if (route.path == "/") {
					ready.value = true;
					if (user.isActive) {
						router.push("/ssa");
					} else {
						router.push("/request-access");
					}
				}

				ready.value = true;
			}
		};
		onMounted(async () => {
			const instance = getCurrentInstance();
			const i18n = instance?.proxy?.$i18n;
			const cachedLanguageSetting = localStorage.getItem("user-prefs-language") as string;
			if (i18n && cachedLanguageSetting) {
				// This allows the login screen to be translated, based off the last user to login
				i18n.locale = cachedLanguageSetting;
			}
			localStorage.removeItem("sessionUrl");
			await router.isReady();

			const checkLoginStatus = _.debounce(async () => {
				if (!document.hidden) {
					const token: string | null =
						localStorage.getItem("mineportal-oauth-token") ||
						localStorage.getItem("mineportal-auth-token");
					if (
						token == null &&
						route.path != "/" &&
						route.path != "/reset-password" &&
						route.path != "/forgot"
					) {
						return authStore.logout();
					}
					if (token != null) {
						// eslint-disable-next-line
						let decoded: any = decode(token!);
						if (authStore.user) {
							if (decoded.email == null) {
								if (
									authStore.user.email !=
									decoded["https://www.imdexlimited.com/email"]
								) {
									// window.location.reload();
								}
							} else {
								if (authStore.user.email != decoded.email) {
									//  window.location.reload();
								}
							}
						}
					}
				}
			}, 100);
			window.addEventListener("focus", checkLoginStatus);
			window.addEventListener("visibilitychange", checkLoginStatus);
			const isAuth0: LocationQueryValue | LocationQueryValue[] = route.query.auth0;
			if (localStorage.getItem("mineportal-oauth-token") != null) {
				axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem(
					"mineportal-oauth-token"
				)}`;
			}
			if (localStorage.getItem("mineportal-auth-token") != null) {
				axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem(
					"mineportal-auth-token"
				)}`;
			}
			if (
				isAuth0 == null &&
				localStorage.getItem("mineportal-auth-token") == null &&
				localStorage.getItem("mineportal-oauth-token") == null
			) {
				ready.value = true;
			}

			watch(authStore, async (value) => {
				if (value.user) {
					if (!Object.keys(value.user).length) {
						eulaDialog.value = false;
					} else {
						if (value.user.isActive) {
							value.user.eulaAcceptedDateTime
								? (eulaDialog.value = false)
								: (eulaDialog.value = true);
						}
					}
				} else {
					eulaDialog.value = false;
				}
			});

			watch(auth0.user, async (currentValue) => {
				if (currentValue == null) return;
				try {
					if (currentValue != null && currentValue.email != null) {
						// eslint-disable-next-line
						try {
							const token = await auth0.getAccessTokenSilently();
							localStorage.setItem("mineportal-oauth-token", token);
							localStorage.removeItem("mineportal-auth-token");
							api.setApiToken(token);
							axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
							const { data: user } = await api.get(
								`/user/read/me?auth0=true&includeUserPreferences=true`
							);

							if (user.userPreferences.language != null) {
								localStorage.setItem(
									"user-prefs-language",
									user.userPreferences.language
								);
								if (i18n) {
									i18n.locale = user.userPreferences.language;
								}
							}

							await siteStore.init();

							if (!siteStore.sites.length && user.isActive) {
								throw new Error(CustomErrorCodes.SiteMissing);
							}

							if (user.isActive) {
								if (localStorage.getItem("mineportal-active-site") == null) {
									siteStore.SET_ACTIVE_SITE(user.userPreferences.defaultSite);
									if (user.userPreferences.defaultSite == null) {
										if (siteStore.sites[0].id != null) {
											await api.put({
												endpoint: "/user-preferences/update",
												post: { defaultSite: siteStore.sites[0].id }
											});
											siteStore.SET_ACTIVE_SITE(siteStore.sites[0].id);
										}
									}
								} else {
									siteStore.SET_ACTIVE_SITE(
										localStorage.getItem("mineportal-active-site") as string
									);
								}
							}

							await authStore.setUser(user);
							authStore.setTimeoutInterval();
							setIsReady(user);
						} catch (e) {
							if (e instanceof AxiosError) {
								if (e.response)
									if (e.response.status == 401) {
										//   window.alert('Your account has been disabled. Please contact a Mineportal administrator for assistance.')
										hasError.value = true;
										authStore.logout();
									}
								// ready.value = true;
							} else if (e instanceof Error) {
								switch (e.message) {
									case CustomErrorCodes.SiteMissing:
										authStore.logout();
										localStorage.setItem(
											"login-error",
											CustomErrorCodes.SiteMissing
										);
										break;
									default:
								}
							}
						}
					}
				} catch (e) {
					console.log(e);
				}
			});
			const token =
				localStorage.getItem("mineportal-auth-token") ||
				localStorage.getItem("mineportal-oauth-token");
			if (token != null) {
				axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
				api.setApiToken(token);
				try {
					// eslint-disable-next-line
					const { data: me }: { data: any } = await api.get(
						"/user/read/me?includeUserPreferences=true"
					);

					authStore.setUser(me);
					authStore.setTimeoutInterval();
					if (me.userPreferences.language != null) {
						localStorage.setItem("user-prefs-language", me.userPreferences.language);
						if (i18n) {
							i18n.locale = me.userPreferences.language;
						}
					}
					setIsReady(me);
					await siteStore.init();

					if (!siteStore.sites.length && me.isActive) {
						return authStore.logout();
					}

					if (localStorage.getItem("mineportal-active-site") == null) {
						if (me.userPreferences.defaultSite == null) {
							if (siteStore.sites[0].id != null) {
								await api.put({
									endpoint: "/user-preferences/update",
									post: { defaultSite: siteStore.sites[0].id }
								});
								siteStore.SET_ACTIVE_SITE(siteStore.sites[0].id);
							}
						} else {
							siteStore.SET_ACTIVE_SITE(me.userPreferences.defaultSite);
						}
					} else {
						const activeSite = localStorage.getItem("mineportal-active-site");
						const canAccessActiveSite = siteStore.sites.find((site: any) => {
							return site.id == activeSite;
						});

						if (!canAccessActiveSite) {
							siteStore.SET_ACTIVE_SITE(siteStore.sites[0].id);
						} else {
							siteStore.SET_ACTIVE_SITE(activeSite as string);
						}
					}
				} catch (e) {
					authStore.logout();
				}

				if (authStore.user?.isActive) {
					authStore.user?.eulaAcceptedDateTime
						? (eulaDialog.value = false)
						: (eulaDialog.value = true);
				}
			} else {
				if (
					route.path != "/activate-account" &&
					route.path != "/request-access" &&
					route.path != "/reset-password"
				) {
					//siteStore.init();
					setTimeout(() => {
						router.push("/");
					}, 100);
				}
			}
		});

		return {
			ready,
			hasError,
			eulaDialog,
			api,
			authStore,
			configStore,
			toast,
			pvw,
			ssaStore,
			wsLink,
			saveState,
			active,
			busy
		};
	},
	computed: {
		clarityConfig(): any {
			return this.configStore.config?.observability?.clarity ?? { enabled: false };
		}
	},
	async created() {
		await this.configStore.init();
		if (this.clarityConfig.enabled) {
			const script = document.createElement("script");
			script.type = "text/javascript";
			script.text = `
				(function(c,l,a,r,i,t,y){
					c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
					t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
					y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
				})(window, document, "clarity", "script", "${this.clarityConfig.key}");
			`;
			document.getElementsByTagName("head")[0].appendChild(script);
		}
	},
	async mounted() {
		this.recordIdleState();

		if (localStorage.getItem("reconnected") === "true") {
			this.toast.toast({
				message: "",
				title: this.$t("session.reconnected"),
				dismissable: false,
				type: "success"
			});

			localStorage.removeItem("reconnected");
		}
	},
	data() {
		return {
			agreementPdf: "",
			agreementName: "",
			isWsLinkDisconnectedWithoutState: false,
			isWsLinkDisconnectedWithState: false,
			resetLoading: false,
			idleState: null as any
		};
	},
	watch: {
		eulaDialog(v) {
			if (v) {
				this.getAgreementPdf();
			}
		},
		"wsLink.IS_WSLINK_DISCONNECTED": function (v) {
			this.isWsLinkDisconnectedWithoutState = v && !this.idleState;

			if (v && this.idleState) {
				this.isWsLinkDisconnectedWithState = true;
			}
		},
		"busy.BUSY_IS": async function (v) {
			if (
				!v &&
				localStorage.getItem("reconnected") &&
				localStorage.getItem("reconnected") !== "true"
			) {
				await this.loadSavedStateFromDisconnect();
			}
		}
	},
	methods: {
		recordIdleState() {
			let timer: any;
			window.onload = resetTimer;
			window.onmousemove = resetTimer;
			window.onkeydown = resetTimer;
			const saveIdleState = async () => {
				if (this.active.SSA_ACTIVE_DATASET) {
					const currentState = await this.saveState.SSA_APPLICATION_STATE_FETCH();

					if (currentState.hasOwnProperty("views")) {
						if (this.saveState.SSA_ACTIVE_SAVE_STATE) {
							this.idleState = {
								...this.saveState.SSA_ACTIVE_SAVE_STATE,
								appState: currentState
							};
						} else {
							this.idleState = {
								appState: currentState
							};
						}
					}
				}
			};
			const resetIdleState = () => {
				if (!this.isWsLinkDisconnectedWithState) {
					this.idleState = null;
				}
			};
			function resetTimer() {
				clearTimeout(timer);
				resetIdleState();
				timer = setTimeout(saveIdleState, 60000); // Record when idle for one minute
			}
		},
		async prepareLoadSaveStateFromDisconnect() {
			if (this.idleState) {
				localStorage.setItem("reconnected", JSON.stringify(this.idleState));
			}

			await this.reset();
		},
		async loadSavedStateFromDisconnect() {
			if (localStorage.getItem("reconnected")) {
				const idleState = JSON.parse(localStorage.getItem("reconnected") as string);

				try {
					await this.saveState.SSA_APPLICATION_STATE_LOAD(idleState);
				} catch (err: any) {
					localStorage.removeItem("reconnected");
					this.reset();
					return;
				}

				if (!idleState.hasOwnProperty("id")) {
					await this.saveState.SSA_SET_ACTIVE_SAVE_STATE(null);
				}

				this.$nextTick(() => {
					setTimeout(() => {
						this.active.SSA_REFRESH_ACTIVE_DATASET();
					}, 100);
				});
				localStorage.removeItem("reconnected");

				this.toast.toast({
					message: "",
					title: this.$t("session.reconnected"),
					dismissable: false,
					type: "success"
				});
			}
		},
		async reset() {
			try {
				this.resetLoading = true;
				await localStorage.removeItem("sessionUrl");
				if (!this.idleState) {
					await localStorage.setItem("reconnected", "true");
				}
				await this.ssaStore.SSA_RESET_SESSION();
				await this.wsLink.SSA_REQUEST_NEW_PARAVIEW(false, false);
				await this.wsLink.SSA_RESET_STORE();
				window.location.reload();
			} catch (e) {
				console.log(e);
			}
		},
		async getAgreementPdf() {
			const agreementData = await this.api.get("agreement/read/imdex-global-eula");
			const { data } = agreementData;
			this.agreementName = data.name;

			const response = await this.api.get("agreement/imdex-global-eula", {
				responseType: "blob"
			});
			const blob = new Blob([response.data], { type: "application/pdf" });
			const objectUrl = URL.createObjectURL(blob);

			this.agreementPdf = objectUrl;
			const pdfElement = document.getElementById("agreement-pdf");
			(pdfElement as HTMLElement).onload = () => URL.revokeObjectURL(objectUrl);
		},
		async acceptAgreement() {
			try {
				const { data } = await this.api.put({
					endpoint: "/user/update",
					post: {
						id: this.authStore.user?.id,
						eulaAccepted: true
					}
				});

				if (this.authStore.user) {
					this.authStore.user.eulaAcceptedDateTime = data.eulaAcceptedDateTime;
				}

				this.eulaDialog = false;
				this.toast.toast({
					message: "",
					title: this.$t("eula.accepted"),
					dismissable: false,
					type: "success"
				});
			} catch (err: any) {
				this.toast.toast({
					message: "",
					title: this.$t("eula.acceptError"),
					dismissable: false,
					type: "error"
				});
			}
		},
		logout() {
			this.authStore.logout();
		}
	},
	name: "App",
	components: {
		AppLayout
	}
});
